<script lang="ts" setup>

import {getLastMessageDate} from "@src/helpers/lastMessageHelper";
import {whatsAppParser} from "@src/helpers/messageHelper";
import {resolveUrl, shorten} from "@src/helpers/utils";
import {computed} from "vue";
import useStore from "@src/stores/store";
import {useChatSessionStore} from "@src/stores/chatSession";
import ChatMessage from "@src/types/ChatMessage";
import {useMessageStore} from "@src/stores/messages";

const props = defineProps<{
    id: number;
    subject: string;
    picture: string;
    lastMessageDate?: Date | null;
    lastMessage?: ChatMessage;
    isCommunity: boolean;
    hasNewMessage?: boolean;
}>();

const store = useStore()
const sessionStore = useChatSessionStore()
const messageStore = useMessageStore()

const lastMessageDate = computed(() => {
    if (!props.lastMessageDate) return "";
    return getLastMessageDate(props.lastMessageDate as any);
});

const lastMessageSenderName = computed(() => {
    const msg: any = props.lastMessage;
    if (!msg) return "";

    return (
        msg.sender?.name ||
        msg.user?.name ||
        msg.costumer?.name ||
        ""
    );
});

const getIconUrl = (iconName: string) => {
    return new URL(`/src/assets/vectors/logo_${iconName}.svg`, import.meta.url).href;
}

const itemClass = computed(() => {
    let base = 'flex items-center px-4 py-2 md:py-6 cursor-pointer md:rounded-sm transition-colors w-full relative border-b-gray-200 border-b-1 md:border-b-0 last:border-b-0 '

    if (props.hasNewMessage) {
        return base + 'bg-green-100 text-white hover:bg-green-200 dark:bg-[#144d37] dark:hover:bg-[#195e42]'
    }

    if (store.settings.darkMode) {
        base += 'text-white hover:bg-gray-700 '
        if (sessionStore.selectedSession && sessionStore.selectedSession.id === props.id) {
            base += 'bg-gray-700 '
        }
    } else {
        base += 'hover:bg-gray-50 '
        if (sessionStore.selectedSession && sessionStore.selectedSession.id === props.id) {
            base += 'bg-gray-100 '
        }
    }

    return base
})
</script>

<template>
    <div :class="itemClass">
        <div class="relative flex-shrink-0">
            <div
                :style="{ backgroundImage: `url(${resolveUrl(props.picture)})` }"
                class="w-8 h-8 rounded-full bg-cover bg-center"
            ></div>
        </div>

        <div class="flex flex-col my-2 justify-between items-baseline ml-4 min-w-0 flex-1">
            <h3 class="text-sm font-semibold text-slate-800 truncate dark:text-white">
                {{ shorten(props.subject, 25) }}
            </h3>
            <div class="flex flex-row items-center mt-1 mb-4 gap-2 flex-wrap">
                <div
                    v-if="lastMessageSenderName"
                    class="text-sm text-slate-800 truncate dark:text-white">
                    {{ shorten(lastMessageSenderName, 25) }}:
                </div>

                <div v-if="props.lastMessage && props.lastMessage.type === 'text'"
                     class="text-sm text-gray-500 truncate pr-2 dark:text-gray-300"
                     v-html="whatsAppParser(shorten(props.lastMessage?.content, 25))">
                </div>
                <div v-else class="text-sm text-gray-500 truncate pr-2 dark:text-gray-300">
                    {{ messageStore.getLastMessagePreview(props.lastMessage!) }}
                </div>
            </div>
        </div>
        <div class="flex flex-col justify-between items-baseline ml-4 min-w-0 flex-shrink-0">
            <span class="text-xs text-gray-400 absolute right-6 whitespace-nowrap dark:text-white">
                    {{ lastMessageDate }}
            </span>
            <div v-if="hasNewMessage"
                 class="w-[1.1rem] h-[1.1rem] rounded-full bg-green-400 animate-pulse mt-4">
            </div>
        </div>
        <div v-if="props.isCommunity" class="flex flex-row items-center absolute bottom-2 right-4">
            <span
                class="text-[10px] font-medium rounded-full  bg-indigo-100 text-indigo-800 py-0.5 px-2.5 border border-indigo-200">
                        Comunidade
            </span>
        </div>
    </div>
</template>